// ─── UTILITIES ───────────────────────────────────────────────────────────────

function getShopifyRoot() {
    return window.Shopify?.routes?.root ?? "/";
}

function uniq(arr) {
    return [...new Set(arr.filter(Boolean))];
}

function getViewMode() {
    return document.getElementById("product-grid")?.getAttribute("data-view-mode") ?? "grid";
}

// ─── CART SECTION SPEC (cached) ───────────────────────────────────────────────

let _cartRenderSpec = null;
function getCartRenderSpec() {
    if (_cartRenderSpec) return _cartRenderSpec;
    const drawer = document.querySelector("cart-drawer");
    const list = drawer?.getSectionsToRender?.();
    _cartRenderSpec = (Array.isArray(list) && list.length && list.every(x => x?.section && x?.selector))
        ? list
        : [{ section: "cart-drawer", selector: ".drawer__inner" }];
    return _cartRenderSpec;
}

let _sectionsToRequest = null;
function getSectionsToRequest() {
    if (_sectionsToRequest) return _sectionsToRequest;
    const spec = getCartRenderSpec();
    _sectionsToRequest = [...new Set(spec.map(s => s.section).filter(Boolean))];
    return _sectionsToRequest;
}

// ─── CART API ────────────────────────────────────────────────────────────────

const root = getShopifyRoot(); // computed once

async function fetchCart() {
    const res = await fetch(`${root}cart.js`, { headers: { Accept: "application/json" } });
    return res.json();
}

// Sections are embedded in the add/change response — no second fetch needed
function extractSections(responseData) {
    const sections = responseData?.sections;
    if (sections && typeof sections === "object" && Object.keys(sections).length) {
        return sections;
    }
    return null;
}

async function addVariantToCart(variantId, quantity = 1) {
    const sections = getSectionsToRequest();
    const res = await fetch(`${root}cart/add.js`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
            items: [{ id: Number(variantId), quantity: Number(quantity) }],
            sections,
            sections_url: window.location.pathname
        })
    });

    const added = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(added?.description || added?.message || "Could not add this item to the cart.");

    // cart/add.js returns sections + item_count directly — no extra fetch needed
    const cart = {
        item_count: added.items?.reduce((s, i) => s + i.quantity, 0) ?? 0,
        items: added.items ?? [],
        sections: extractSections(added) ?? {}
    };
    return cart;
}

async function changeLineItemQuantityByKey(key, quantity) {
    const sections = getSectionsToRequest();
    const res = await fetch(`${root}cart/change.js`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ id: key, quantity, sections, sections_url: window.location.pathname })
    });

    const cart = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(cart?.description || cart?.message || "Could not update cart quantity.");
    if (!cart.sections) cart.sections = {};
    return cart;
}

// ── OPTIMISTIC UPSERT ──────────────────────────────────────────────────────────
// Always fire cart/add first (no preflight cart.js fetch).
// Shopify will merge qty if variant already exists — then we reconcile in background.
async function upsertVariantToCart(variantId) {
    const sections = getSectionsToRequest();

    const res = await fetch(`${root}cart/add.js`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
            items: [{ id: Number(variantId), quantity: 1 }],
            sections,
            sections_url: window.location.pathname
        })
    });

    const added = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(added?.description || added?.message || "Could not add this item to the cart.");

    // CWI CODE N
    cartIconBubble();
    
    return {
        item_count: added.items?.reduce((s, i) => s + i.quantity, 0) ?? 0,
        items: added.items ?? [],
        sections: extractSections(added) ?? {}
    };
}

// ─── CART DOM ────────────────────────────────────────────────────────────────

function openCartDrawer() {
    const drawer = document.querySelector("cart-drawer");
    if (drawer && typeof drawer.open === "function") drawer.open();
}

function getItemCountFromState(state) {
    if (typeof state?.item_count === "number") return state.item_count;
    if (Array.isArray(state?.items)) return state.items.reduce((s, i) => s + (Number(i.quantity) || 0), 0);
    return 0;
}

function syncCartDrawerEmptyState(state) {
    const drawerEl = document.querySelector("cart-drawer");
    if (!drawerEl) return;
    const isEmpty = getItemCountFromState(state) === 0;
    drawerEl.classList.toggle("is-empty", isEmpty);
    drawerEl.querySelectorAll(".drawer, .drawer__inner, .drawer__content, .cart-drawer, .cart-drawer__content")
        .forEach(el => el.classList.toggle("is-empty", isEmpty));
}

function applyCartSections(state) {
    const sections = state?.sections;
    if (!sections) return;

    const spec = getCartRenderSpec();
    const drawer = document.querySelector("cart-drawer");
    const parser = new DOMParser();

    spec.forEach(({ section, selector }) => {
        const html = sections[section];
        if (!html) return;
        const src = parser.parseFromString(html, "text/html").querySelector(selector);
        if (!src) return;
        const dst = drawer?.querySelector(selector) ?? document.querySelector(selector);
        if (dst) dst.innerHTML = src.innerHTML;
    });

    syncCartDrawerEmptyState(state);
    document.dispatchEvent(new CustomEvent("cart:updated", { detail: state }));
}

// ─── CART OVERLAY GUARD ───────────────────────────────────────────────────────

function forceClearDrawerOverlay() {
    document.body.classList.remove("overflow-hidden", "no-scroll", "prevent-scroll");
    document.documentElement.classList.remove("overflow-hidden");
    document.querySelectorAll(".drawer__overlay, #CartDrawer-Overlay, .cart-drawer__overlay").forEach(el => {
        el.classList.remove("is-active", "active", "open");
        el.style.display = "none";
    });
}

function installCartOverlayGuard() {
    const drawer = document.querySelector("cart-drawer");
    if (!drawer || drawer.dataset.overlayGuard === "1") return;
    drawer.dataset.overlayGuard = "1";

    const sync = () => {
        const open = drawer.hasAttribute("open") || drawer.classList.contains("is-open") || drawer.classList.contains("active");
        if (!open) forceClearDrawerOverlay();
    };

    new MutationObserver(sync).observe(drawer, { attributes: true, attributeFilter: ["open", "class"] });
    document.addEventListener("click", e => { if (e.target.closest("cart-drawer")) setTimeout(sync, 0); }, true);
    document.addEventListener("keyup", e => { if (e.key === "Escape") setTimeout(sync, 0); });
}

// ─── PRODUCT DATA ─────────────────────────────────────────────────────────────

function getProductRows(productItem) {
    const raw = productItem?.dataset?.product;
    if (!raw) return [];
    try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) return parsed.map(String).map(s => s.trim()).filter(Boolean);
        if (typeof parsed === "string") return parsed.split(",").map(s => s.trim()).filter(Boolean);
    } catch (_) {}
    return String(raw).split(",").map(s => s.trim()).filter(Boolean);
}

// ─── PRODUCT IMAGE CACHE ──────────────────────────────────────────────────────

const productJsonCache = new Map();

async function getProductJson(handle) {
    if (productJsonCache.has(handle)) return productJsonCache.get(handle);
    const res = await fetch(`/products/${handle}.js`);
    if (!res.ok) throw new Error(`Product JSON fetch failed: ${res.status}`);
    const data = await res.json();
    productJsonCache.set(handle, data);
    return data;
}

async function updateImages(productItem, selectedColor, productHandle, variantId = null) {
    try {
        const product = await getProductJson(productHandle); // cached after first call
        const allImageMedia = (product.media || []).filter(m => m.media_type === "image");
        const allImages = allImageMedia.map(m => m.preview_image?.src).filter(Boolean);

        const colorLower = String(selectedColor ?? "").trim().toLowerCase();
        const byAlt = allImageMedia
            .filter(m => (m.alt ?? "").trim().toLowerCase() === colorLower)
            .map(m => m.preview_image?.src)
            .filter(Boolean);

        let finalList;
        if (byAlt.length) {
            finalList = uniq(byAlt);
        } else if (variantId && Array.isArray(product.variants)) {
            const v = product.variants.find(v => String(v.id) === String(variantId));
            finalList = uniq(v?.featured_image?.src ? [v.featured_image.src, ...allImages] : allImages);
        } else {
            finalList = uniq(allImages);
        }

        const imageContainer = productItem.querySelector(".image-container");
        if (!imageContainer) return;

        // Build fragment — single DOM write
        const frag = document.createDocumentFragment();
        const isList = getViewMode() === "list";
        finalList.forEach((src, index) => {
            const img = document.createElement("img");
            img.src = src;
            img.loading = "lazy";
            img.className = "product-img";
            if ((isList && index === finalList.length - 1) || (!isList && index === 0)) img.classList.add("visible");
            frag.appendChild(img);
        });

        imageContainer.innerHTML = "";
        imageContainer.appendChild(frag);
    } catch (err) {
        console.error("[updateImages] failed:", err);
    }
}

// ─── SIZE MODAL HELPERS ───────────────────────────────────────────────────────

function showSizeError(modalEl, message) {
    if (!modalEl) return;
    let box = modalEl.querySelector(".size-modal-error");
    if (!box) {
        box = Object.assign(document.createElement("div"), {
            className: "size-modal-error font-alt-heading small",
            style: "margin-top:10px"
        });
        box.setAttribute("aria-live", "polite");
        modalEl.querySelector(".global-modal__content-info")?.appendChild(box);
    }
    box.textContent = message;
}

function closeModalAnimated(modalEl, delayMs = 120, animMs = 180) {
    if (!modalEl) return;
    setTimeout(() => {
        modalEl.classList.add("is-closing");
        setTimeout(() => {
            const closeBtn = modalEl.querySelector(".global-modal-modal__toggle");
            if (closeBtn) closeBtn.click();
            else if (typeof modalEl.close === "function") modalEl.close();
            else if (typeof modalEl.hide === "function") modalEl.hide();
            modalEl.classList.remove("is-closing");
            modalEl.setAttribute("aria-hidden", "true");
        }, animMs);
    }, delayMs);
}

// ─── SIZES ────────────────────────────────────────────────────────────────────

function setCardVariantUrl(productItem, variantId) {
    const handle = productItem.dataset.handle;
    const base = productItem.dataset.productUrlBase ?? `${root}products/${handle}`;
    const url = variantId ? `${base}?variant=${variantId}` : base;
    productItem.dataset.url = url;

    const modalSelector = productItem.querySelector("modal-opener[data-modal]")?.dataset.modal;
    const modalLink = modalSelector ? document.querySelector(modalSelector)?.querySelector(".global-modal__content-info a") : null;
    if (modalLink) modalLink.href = url;
}

function updateSizes(productItem, selectedColor) {
    if (!productItem) return;

    const rows = getProductRows(productItem);
    if (!selectedColor) selectedColor = (rows[0] ?? "").split("|")[0];
    const chosen = String(selectedColor).trim().toLowerCase();

    const sizes = rows
        .map(row => String(row).replace(/^"|"$/g, "").split("|"))
        .filter(parts => parts.length >= 4 && String(parts[0] ?? "").trim().toLowerCase() === chosen)
        .map(parts => ({ label: parts[1], available: parts[2].trim() === "true", variantId: String(parts[3] ?? "").trim() }));

    const modalSelector = productItem.querySelector("modal-opener[data-modal]")?.dataset.modal;
    const modalEl = modalSelector ? document.querySelector(modalSelector) : null;
    const targets = [
        productItem.querySelector(".product_image .product_sizes"),
        modalEl?.querySelector(".product_sizes")
    ].filter(Boolean);

    if (!targets.length) return;

    targets.forEach(container => {
        const frag = document.createDocumentFragment();
        sizes.forEach(size => {
            const btn = document.createElement("button");
            btn.type = "button";
            btn.textContent = size.label;
            btn.className = "size-option font-alt-heading small";
            btn.disabled = !size.available;
            btn.addEventListener("click", async (e) => {
                e.preventDefault();
                e.stopPropagation();
                btn.disabled = true;
                if (modalEl) { const old = modalEl.querySelector(".size-modal-error"); if (old) old.textContent = ""; }

                // ── Open drawer & close modal IMMEDIATELY (don't wait for network) ──
                openCartDrawer();
                closeModalAnimated(modalEl, 60, 120);

                try {
                    const state = await upsertVariantToCart(size.variantId);
                    applyCartSections(state); // patches drawer content once response arrives
                } catch (err) {
                    btn.disabled = false;
                    showSizeError(modalEl, err.message);
                }
            });
            frag.appendChild(btn);
        });
        container.innerHTML = "";
        container.appendChild(frag);
    });
}

// ─── VIEW MODE TOGGLE ─────────────────────────────────────────────────────────

function updateViewMode(grid, mode) {
    grid.querySelectorAll(".product_item").forEach(item => {
        const images = item.querySelectorAll(".product_image .product-img");
        if (!images.length) return;
        const controls = item.querySelector(".carousel-controls");
        images.forEach(img => img.classList.remove("visible"));
        if (mode === "list") {
            images[images.length - 1].classList.add("visible");
            if (controls) controls.style.display = "none";
        } else {
            images[0].classList.add("visible");
            if (controls) controls.style.display = "flex";
        }
    });
}

document.querySelectorAll(".model-toggle-button").forEach(button => {
    button.addEventListener("click", function () {
        if (this.classList.contains("is-active")) return;
        document.querySelectorAll(".model-toggle-button").forEach(b => b.classList.remove("is-active"));
        this.classList.add("is-active");
        const grid = document.getElementById("product-grid") ?? document.getElementById("wishlist-grid");
        if (!grid) return;
        const newMode = this === document.querySelectorAll(".model-toggle-button")[0] ? "grid" : "list";
        grid.setAttribute("data-view-mode", newMode);
        updateViewMode(grid, newMode);

        // CWI CODE N
        customLazyLoad();
    });
});

// ─── CARD BINDING ─────────────────────────────────────────────────────────────

function bindOneCard(productItem) {
    if (!productItem || productItem.dataset.bound === "true") return;
    productItem.dataset.bound = "true";

    const defaultColor = productItem.dataset.defaultColor
        ?? productItem.querySelector(".color-swatch.is-active")?.dataset.color;
    updateSizes(productItem, defaultColor);

    const active = productItem.querySelector(".color-swatch.is-active");
    if (active?.dataset.variantId) setCardVariantUrl(productItem, active.dataset.variantId);

    productItem.addEventListener("click", function (e) {
        const ignore = [".carousel-controls", ".product_sizes", ".color-swatches", ".color-swatch",
            ".product_variants", ".product_variants_wrapper", "modal-opener", "modal-dialog", ".button-popup-option"];
        if (!ignore.some(sel => e.target.closest(sel))) {
            const url = productItem.dataset.url;
            if (url) window.location.href = url;
        }
    });
}

function bindAllCards(root = document) {
    root.querySelectorAll(".product_item").forEach(bindOneCard);
}

// ─── IMAGE CAROUSEL ───────────────────────────────────────────────────────────

function changeImage(button, direction) {
    const productImage = button.closest(".product_image");
    const images = productImage.querySelectorAll(".product-img");
    const index = [...images].findIndex(img => img.classList.contains("visible"));
    if (index === -1) return;
    images[index].classList.remove("visible");
    const len = images.length;
    const newIndex = direction === "prev" ? (index - 1 + len) % len : (index + 1) % len;
    images[newIndex].classList.add("visible");
}

// ─── DELEGATED EVENT HANDLERS ─────────────────────────────────────────────────

// Swatch clicks — single delegated listener on document (capture)
document.addEventListener("click", async (e) => {
    const swatch = e.target.closest(".product_item .color-swatch");
    if (!swatch) return;
    e.preventDefault();
    e.stopPropagation();

    const productItem = swatch.closest(".product_item");
    if (!productItem) return;

    productItem.querySelectorAll(".color-swatch").forEach(s => s.classList.remove("is-active"));
    swatch.classList.add("is-active");

    const { color: selectedColor, variantId } = swatch.dataset;
    if (variantId) setCardVariantUrl(productItem, variantId);

    // Run sizes sync + image fetch in parallel
    updateSizes(productItem, selectedColor);
    updateImages(productItem, selectedColor, productItem.dataset.handle, variantId);
}, true);

// Modal opener — update sizes on open
document.addEventListener("click", (e) => {
    const openerBtn = e.target.closest(".size-modal--menu-opener button");
    if (!openerBtn) return;
    const productItem = openerBtn.closest(".product_item");
    if (!productItem) return;
    const color = productItem.querySelector(".color-swatch.is-active")?.dataset.color ?? productItem.dataset.defaultColor;
    updateSizes(productItem, color);
});

// ─── MUTATION OBSERVER (narrower scope) ───────────────────────────────────────

// Watch body instead of documentElement, and only childList at top level + subtree for product grids
const mo = new MutationObserver((mutations) => {
    for (const m of mutations) {
        for (const node of m.addedNodes) {
            if (!(node instanceof Element)) continue;
            if (node.matches(".product_item")) bindOneCard(node);
            else {
                const items = node.querySelectorAll?.(".product_item");
                if (items?.length) items.forEach(bindOneCard);
            }
        }
    }
});
mo.observe(document.body, { childList: true, subtree: true });

// ─── INIT ─────────────────────────────────────────────────────────────────────

document.addEventListener("DOMContentLoaded", () => {
    bindAllCards();
    installCartOverlayGuard();
});

document.addEventListener("shopify:section:load", e => bindAllCards(e.target));
document.addEventListener("shopify:section:select", e => bindAllCards(e.target));

window.addEventListener("resize", () => {
    if (typeof adjustImageContainers === "function") adjustImageContainers();
    if (typeof adjustStrawberryImageContainers === "function") adjustStrawberryImageContainers();
});